import React, { useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import { sanitizeQueryParam } from '@v2/lib';
import { Button } from '@v2/components';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import {
  usePims,
  usePimsDetail,
  usePimsConfigurations,
  useApps,
  useToggleConnectivity,
  useUpdatePimsAppRelation,
  useUpdatePimsConfig,
  useTableFilters,
} from '@v2/hooks';
import { CreatePimsForm, CreatePimsConfigForm } from '@v2/features';
import type { Pims, PimsConfiguration, UpdatePimsConfigPayload } from '@v2/types';
import { PimsTable, PimsDetails, PimsConfigTable } from './components';
import { styles } from './styles';

const PimsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const selectedPimsId = sanitizeQueryParam(searchParams.get('pimsId'));

  const [isCreatePimsOpen, setIsCreatePimsOpen] = useState(false);
  const [isCreateConfigOpen, setIsCreateConfigOpen] = useState(false);
  const [configSearch, setConfigSearch] = useState('');
  const [updatingConfigId, setUpdatingConfigId] = useState<string | null>(null);

  const { pims: pimsList, loading: pimsLoading, error: pimsError } = usePims();
  const {
    pims: selectedPims,
    loading: detailLoading,
    error: detailError,
  } = usePimsDetail(selectedPimsId);
  const { configurations } = usePimsConfigurations(selectedPimsId);
  const { apps, loading: appsLoading } = useApps();
  const { toggleConnectivity, loading: connectivityLoading } = useToggleConnectivity();
  const { updateAppRelation, loading: appRelationLoading } = useUpdatePimsAppRelation();
  const { updateConfig } = useUpdatePimsConfig();

  const { filteredData, search, setSearch, sortConfig, handleSort } = useTableFilters({
    data: pimsList,
    searchKeys: ['name'],
  });

  const handleSelectPims = useCallback(
    (pims: Pims) => {
      const params = new URLSearchParams(location.search);
      params.set('pimsId', pims.id);
      history.push({ search: params.toString() });
      setConfigSearch('');
    },
    [history, location.search],
  );

  const handleClosePanel = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('pimsId');
    history.push({ search: params.toString() });
  }, [history, location.search]);

  const handleToggleConnectivity = useCallback(async () => {
    if (!selectedPimsId) return;
    try {
      await toggleConnectivity(selectedPimsId);
      toast.success('Connectivity toggled');
    } catch {
      toast.error('Failed to toggle connectivity');
    }
  }, [selectedPimsId, toggleConnectivity]);

  const handleUpdateApp = useCallback(
    async (appId: string) => {
      if (!selectedPimsId) return;
      try {
        await updateAppRelation({ id: selectedPimsId, appId });
        toast.success('App updated');
      } catch {
        toast.error('Failed to update app');
      }
    },
    [selectedPimsId, updateAppRelation],
  );

  const handleUpdateConfig = useCallback(
    async (config: PimsConfiguration, newValue: string) => {
      setUpdatingConfigId(config.id);
      try {
        const payload: UpdatePimsConfigPayload = {
          id: config.id,
          entityId: config.entityId,
          entityType: config.entityType,
          key: config.key,
          value: newValue,
        };
        await updateConfig(payload);
        toast.success('Configuration updated');
      } catch {
        toast.error('Failed to update configuration');
      } finally {
        setUpdatingConfigId(null);
      }
    },
    [updateConfig],
  );

  const handleCreatePimsSuccess = useCallback(
    (newPimsId: string) => {
      const params = new URLSearchParams(location.search);
      params.set('pimsId', newPimsId);
      history.push({ search: params.toString() });
    },
    [history, location.search],
  );

  return (
    <>
      <SettingsPageLayout
        title="Pims"
        actions={
          <Button
            variant="primary"
            onClick={() => setIsCreatePimsOpen(true)}
            icon={<FontAwesomeIcon icon={faPlus} />}>
            Create PIMS
          </Button>
        }>
        <div className={styles.pageContent}>
          <ListDetailView
            selectedId={selectedPimsId}
            list={
              <PimsTable
                pims={filteredData}
                loading={pimsLoading}
                error={pimsError}
                search={search}
                onSearchChange={setSearch}
                sortConfig={sortConfig}
                onSortChange={handleSort}
                selectedPimsId={selectedPimsId}
                onSelectPims={handleSelectPims}
              />
            }
            detail={
              <DetailPanel
                title="PIMS Details"
                onClose={handleClosePanel}
                loading={detailLoading}
                loadingMessage="Loading PIMS details..."
                error={detailError}
                errorMessage="There was an error fetching the PIMS details.">
                {selectedPims && (
                  <PimsDetails
                    pims={selectedPims}
                    apps={apps}
                    appsLoading={appsLoading}
                    onToggleConnectivity={handleToggleConnectivity}
                    connectivityLoading={connectivityLoading}
                    onUpdateApp={handleUpdateApp}
                    appRelationLoading={appRelationLoading}
                    onCreateConfig={() => setIsCreateConfigOpen(true)}
                    configurationsCount={configurations.length}
                    configurationsContent={
                      <PimsConfigTable
                        configurations={configurations}
                        search={configSearch}
                        onSearchChange={setConfigSearch}
                        onUpdateConfig={handleUpdateConfig}
                        updatingConfigId={updatingConfigId}
                      />
                    }
                  />
                )}
              </DetailPanel>
            }
          />
        </div>
      </SettingsPageLayout>

      <CreatePimsForm
        isOpen={isCreatePimsOpen}
        onClose={() => setIsCreatePimsOpen(false)}
        onSuccess={handleCreatePimsSuccess}
      />

      {selectedPimsId && (
        <CreatePimsConfigForm
          isOpen={isCreateConfigOpen}
          onClose={() => setIsCreateConfigOpen(false)}
          pimsId={selectedPimsId}
          onSuccess={() => setIsCreateConfigOpen(false)}
        />
      )}
    </>
  );
};

export default PimsPage;
